"use client";

import "mapbox-gl/dist/mapbox-gl.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import Map, { Marker, NavigationControl, Popup, type MapRef } from "react-map-gl";
import { List, MapIcon } from "lucide-react";
import { Button } from "@/components/v2/Button";
import { MrtLayers, MrtLegend } from "@/components/v2/map/MrtLayers";
import { PropertyCardV2 } from "@/components/v2/PropertyCardV2";
import { EMPTY_SEARCH, SmartSearch, searchStateToQuery, type SearchState } from "@/components/v2/home/SmartSearch";
import { cn } from "@/lib/utils";
import { formatPriceShort, sanePrice, statusMeta } from "@/lib/v2/format";
import { describeNear } from "@/lib/v2/nearPlaces";
import { propertyCoords, propertyHref, toCardProps, type PropertyLite } from "@/lib/v2/property";
import { applySearch, type SortKey } from "@/lib/v2/searchFilter";

const SORTS: Array<{ value: SortKey; label: string }> = [
  { value: "newest", label: "Newest" },
  { value: "price-asc", label: "Price low to high" },
  { value: "price-desc", label: "Price high to low" },
  { value: "top", label: "TOP soonest" },
];

const SG_CENTER = { longitude: 103.8198, latitude: 1.3521, zoom: 10.6 };

export function SearchResults({ all, initial }: { all: PropertyLite[]; initial: SearchState }) {
  const router = useRouter();
  const pathname = usePathname();
  const [state, setState] = useState<SearchState>(initial);
  const [sort, setSort] = useState<SortKey>("newest");
  const [view, setView] = useState<"list" | "map">("list");
  const [hovered, setHovered] = useState<string | null>(null);
  const [popup, setPopup] = useState<PropertyLite | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const mapRef = useRef<MapRef>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => setHydrated(true), []);

  const onChange = useCallback(
    (s: SearchState) => {
      setState(s);
      router.replace(`${pathname}${searchStateToQuery(s)}`, { scroll: false });
    },
    [router, pathname]
  );

  const results = useMemo(() => applySearch(all, state, sort), [all, state, sort]);
  const nearLabel = state.near ? describeNear(state.near) : "";
  const plotted = useMemo(
    () => results.map((p) => ({ p, c: propertyCoords(p) })).filter((x): x is { p: PropertyLite; c: { lat: number; lng: number } } => !!x.c),
    [results]
  );

  // Fit the map to whatever is plotted whenever the result set changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || plotted.length === 0) return;
    const lngs = plotted.map((x) => x.c.lng);
    const lats = plotted.map((x) => x.c.lat);
    map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { padding: 60, maxZoom: 14, duration: 600 }
    );
  }, [plotted]);

  const clear = () => onChange({ ...EMPTY_SEARCH, districts: [] });

  const focusCard = (id: string) => {
    setView("list");
    cardRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="bg-surface pb-16">
      <div className="sticky top-20 z-30 border-b border-line bg-white/95 backdrop-blur">
        <div className="mx-auto max-w-site px-4 py-3 md:px-8">
          <SmartSearch variant="bar" initial={initial} onChange={onChange} />
        </div>
      </div>

      <div className="mx-auto grid max-w-site gap-6 px-4 pt-6 md:px-8 lg:grid-cols-12">
        <div className={cn("lg:col-span-7", view === "map" && "hidden lg:block")}>
          <div className="mb-4 flex items-center justify-between gap-4">
            <h1 className="font-display text-xl font-semibold text-ink">
              {nearLabel
                ? `Launches near ${nearLabel}`
                : `${results.length} ${results.length === 1 ? "project" : "projects"}`}
              {nearLabel && <span className="ml-2 text-base font-normal text-ink-muted">{results.length}</span>}
            </h1>
            <label className="flex items-center gap-2 text-sm text-ink-muted">
              <span className="sr-only sm:not-sr-only">Sort</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                className="rounded-full border border-line bg-white px-3 py-2 text-sm text-ink"
              >
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {!hydrated ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-80 animate-pulse rounded-2xl bg-white" />
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="rounded-2xl border border-line bg-white p-10 text-center">
              <p className="font-display text-lg font-semibold text-ink">
                {nearLabel ? `No launch is listed near ${nearLabel} yet.` : "No projects match those filters yet."}
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <Button variant="secondary" onClick={clear}>
                  Clear filters
                </Button>
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
                <Link href="/#browse" className="font-semibold text-brand-700">Browse by district</Link>
                <Link href="/new-launches?status=upcoming" className="font-semibold text-brand-700">See all upcoming</Link>
                <Link href="/qa" className="font-semibold text-brand-700">Ask the AI</Link>
              </div>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {results.map((p) => (
                <div
                  key={p._id}
                  ref={(el) => {
                    cardRefs.current[p._id] = el;
                  }}
                  onMouseEnter={() => setHovered(p._id)}
                  onMouseLeave={() => setHovered(null)}
                  className={cn("rounded-2xl transition-shadow", hovered === p._id && "shadow-card")}
                >
                  <PropertyCardV2 {...toCardProps(p)} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={cn("lg:col-span-5", view === "list" && "hidden lg:block")}>
          <div className="overflow-hidden rounded-2xl border border-line bg-white lg:sticky lg:top-[9.5rem] lg:h-[calc(100vh-11rem)]">
            <div className="relative h-[70vh] lg:h-full">
              <Map
                ref={mapRef}
                initialViewState={SG_CENTER}
                mapboxAccessToken={process.env.NEXT_PUBLIC_MAP_BOX_KEY}
                mapStyle="mapbox://styles/mapbox/streets-v9"
                style={{ width: "100%", height: "100%" }}
                onClick={() => setPopup(null)}
              >
                <NavigationControl position="top-right" showCompass={false} />
                <MrtLayers />
                {plotted.map(({ p, c }) => {
                  const price = sanePrice(p.min_flat_price);
                  const tone = statusMeta(p.construction_status ?? "", p.isPreLaunch).tone;
                  const active = hovered === p._id || popup?._id === p._id;
                  return (
                    <Marker
                      key={p._id}
                      longitude={c.lng}
                      latitude={c.lat}
                      anchor="bottom"
                      onClick={(e) => {
                        e.originalEvent.stopPropagation();
                        setPopup(p);
                        focusCard(p._id);
                      }}
                    >
                      <button
                        type="button"
                        aria-label={p.title || p.name}
                        onMouseEnter={() => setHovered(p._id)}
                        onMouseLeave={() => setHovered(null)}
                        className={cn(
                          "rounded-full border px-2.5 py-1 text-xs font-semibold shadow-sm transition-transform",
                          active
                            ? "-translate-y-1 border-brand-700 bg-brand-700 text-white"
                            : tone === "upcoming" || tone === "prelaunch"
                            ? "border-brand-100 bg-brand-50 text-brand-700"
                            : "border-line bg-white text-brand-600"
                        )}
                      >
                        {price != null ? formatPriceShort(price) : p.district || "New"}
                      </button>
                    </Marker>
                  );
                })}
                {popup && propertyCoords(popup) && (
                  <Popup
                    longitude={propertyCoords(popup)!.lng}
                    latitude={propertyCoords(popup)!.lat}
                    anchor="top"
                    closeButton={false}
                    onClose={() => setPopup(null)}
                    maxWidth="240px"
                  >
                    <div className="p-1">
                      <p className="text-sm font-semibold text-ink">{popup.title || popup.name}</p>
                      <p className="mt-0.5 text-xs text-ink-muted">
                        {sanePrice(popup.min_flat_price) != null
                          ? `From ${formatPriceShort(sanePrice(popup.min_flat_price))}`
                          : "Price on request"}
                      </p>
                      <Link href={propertyHref(popup)} className="mt-2 inline-block text-xs font-semibold text-brand-700">
                        View project
                      </Link>
                    </div>
                  </Popup>
                )}
              </Map>
              <MrtLegend className="absolute bottom-3 left-3 flex flex-wrap gap-x-2.5 gap-y-1 rounded-full bg-white/90 px-3 py-1.5 shadow" />
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-5 left-1/2 z-30 -translate-x-1/2 lg:hidden">
        <button
          type="button"
          onClick={() => setView((v) => (v === "list" ? "map" : "list"))}
          className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-white shadow-card"
        >
          {view === "list" ? <MapIcon aria-hidden="true" className="size-4" /> : <List aria-hidden="true" className="size-4" />}
          {view === "list" ? "Map" : "List"}
        </button>
      </div>
    </div>
  );
}
